import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { sales_types } from "@/modules/common/constants";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { Options } from "@/modules/common/types";

const dateSchema = z.date({
  required_error: "Required",
  invalid_type_error: "Invalid date",
});

const blockedFormSchema = z.object({
  areaUId: z.string().optional(),
  compoundUId: z.string().min(1, "Required"),
  developerUId: z.string().optional(),
  propertyTypeUId: z.string().min(1, "Required"),
  dateOfType: dateSchema,
  expectedContractionDate: dateSchema,
  expectedValue: z.string().transform((val, ctx) => {
    const parsed = parseInt(val.replace(/[^\d]/g, ""), 10);
    if (isNaN(parsed) || parsed < 1000000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expected value must be greater than 1,000,000",
      });
      return z.NEVER;
    }
    return parsed;
  }),
  cashPrice: z.number().optional(),
  comment: z.string().min(4, "Minimum 4 characters"),
  organizationUId: z.string().optional(),
});

type BlockedFormData = z.infer<typeof blockedFormSchema>;

const useBlockedForm = ({
  selectedUnitLocation,
  handleSaleSubmission,
  saleType,
  existingSale,
}: {
  handleSaleSubmission: (
    formattedData: any,
    formType: "sale" | "form",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    }
  ) => void;
  saleType: string | null;
  existingSale?: any;
  selectedUnitLocation: Options | undefined;
}) => {
  const { selectedOrganization } = useOrganization();

  const { lead } = useContext(LeadPageContext);

  const current_sale_type = sales_types.find((type) => {
    return type.sale_type === saleType;
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<BlockedFormData>({
    resolver: zodResolver(blockedFormSchema),
    defaultValues: {
      areaUId: undefined,
      compoundUId: undefined,
      propertyTypeUId: undefined,
      dateOfType: undefined,
      expectedContractionDate: undefined,
      expectedValue: undefined,
      cashPrice: undefined,
      comment: undefined,
      organizationUId: undefined,
    },
  });

  const selectedCompound = watch("compoundUId");
  const selectedPropertyType = watch("propertyTypeUId");

  useEffect(() => {
    if (existingSale) {
      const areaUId = existingSale?.area?.uid;
      const compoundUId = existingSale?.compound?.uid;

      reset({
        areaUId: areaUId,
        compoundUId: compoundUId,
        propertyTypeUId: existingSale?.propertyType?.uid,
        dateOfType: existingSale[`dateOf${saleType}`]
          ? new Date(existingSale[`dateOf${saleType}`])
          : undefined,
        expectedContractionDate: existingSale.expectedContractionDate
          ? new Date(existingSale.expectedContractionDate)
          : undefined,
        expectedValue: existingSale.expectedValue?.toString(),
        cashPrice: !existingSale.cashPrice ? undefined : existingSale.cashPrice,
        comment: existingSale.comment || "",
        organizationUId: lead?.data?.lead?.organization?.uid,
      });

      setValue("areaUId", areaUId);
      setValue("compoundUId", compoundUId);
    }
  }, [
    existingSale,
    reset,
    setValue,
    saleType,
    lead?.data?.lead?.organization?.uid,
  ]);

  const onSubmit = (data: BlockedFormData) => {
    const cashPrice =
      lead?.data?.lead_type.lead_type === "Nawy Now" ? data.cashPrice : null;

    const formatDate = (date: Date) => {
      return date.toISOString().split("T")[0];
    };
    const formattedData = {
      areaUId: existingSale?.area?.uid || selectedUnitLocation?.optionValueUid,
      compoundUId: data.compoundUId,
      propertyTypeUId: data.propertyTypeUId,
      [`dateOf${saleType}`]: formatDate(data.dateOfType),
      expectedContractionDate: formatDate(data.expectedContractionDate),
      ...(!existingSale && { journeyUId: lead?.data?.lead?.journey_uid }),
      ...(!existingSale && { userUId: lead?.data?.lead?.user?.id }),
      ...(!existingSale && {
        organizationUId:
          lead?.data?.lead?.organization?.uid || selectedOrganization?.uid,
      }),

      expectedValue: Number(data.expectedValue),
      saleTypeName: current_sale_type?.sale_type,
      ...(selectedUnitLocation && { unitLocation: selectedUnitLocation.label }),
      comment: data.comment,

      ...(cashPrice && { cashPrice: cashPrice }),
    };

    if (existingSale) {
      handleSaleSubmission(formattedData, "sale", {
        saleUId: existingSale?.uid,
        leadUId: existingSale?.lead?.uid,
      });
    } else {
      handleSaleSubmission(formattedData, "sale");
    }
  };

  return {
    onSubmit,
    errors,
    control,
    handleSubmit,
    selectedCompound,
    selectedPropertyType,
  };
};

export default useBlockedForm;
